import { useState, useEffect, useCallback } from 'react';
import { floorPlanService } from '@/features/booking/services/floorPlanService';
import type { FloorPlanResponse } from '@/features/booking/types/floorPlanTypes';

interface UseFloorPlansResult {
    floorPlans: FloorPlanResponse[];
    loading: boolean;
    error: string | null;
}

/**
 * Hook to fetch and filter floor plans for a booking category.
 * Filters floor plan items to only include those belonging to the given booking category.
 */
export const useFloorPlans = (
    bookingCategoryId: string | undefined
): UseFloorPlansResult => {
    const [floorPlans, setFloorPlans] = useState<FloorPlanResponse[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchFloorPlans = useCallback(async () => {
        if (!bookingCategoryId) return;

        setLoading(true);
        setError(null);

        try {
            const rawFloorPlans = await floorPlanService.getFloorPlansByBookingCategory(bookingCategoryId);

            // Filter items: only keep those that belong to this booking category
            const filtered = rawFloorPlans
                .map((fp) => ({
                    ...fp,
                    items: fp.items.filter(
                        (item) => item.booking_category_id === bookingCategoryId
                    ),
                }))
                // Only keep floor plans that have at least one relevant item
                .filter((fp) => fp.items.length > 0);

            setFloorPlans(filtered);
        } catch (err) {
            console.error('Error in useFloorPlans:', err);
            setError(err instanceof Error ? err.message : 'Failed to load floor plans');
            setFloorPlans([]);
        } finally {
            setLoading(false);
        }
    }, [bookingCategoryId]);

    useEffect(() => {
        fetchFloorPlans();
    }, [fetchFloorPlans]);

    return { floorPlans, loading, error };
};
